import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  VStack,
  Heading,
  Text,
  useToast,
  Card,
  CardBody,
  HStack,
  Progress,
  IconButton,
  useColorModeValue,
  Flex,
  Stack,
  Center,
  Spinner,
  SimpleGrid,
  Icon,
  Tooltip,
  Badge,
  Kbd,
  ScaleFade,
  SlideFade,
  CircularProgress,
  CircularProgressLabel,
  Image,
  Divider,
  Grid,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useBreakpointValue,
  useColorMode,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Switch,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { FaVolumeUp, FaHeart, FaStar, FaFire, FaTrophy, FaCheck, FaTimes, FaLanguage, FaCrown, FaGamepad, FaFlag, FaBrain } from 'react-icons/fa';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { TTSService } from '../services/tts';
import { TTSApiService } from '../services/ttsApi';
import { apiClient } from "../lib/apiClient";
import { useTranslation } from "react-i18next";
import {
  FiPlus,
  FiBook,
  FiCheckCircle,
  FiMessageSquare,
  FiList,
  FiBarChart2,
  FiSkipForward,
  FiEye,
  FiEdit,
  FiXCircle,
} from "react-icons/fi";
import confetti from "canvas-confetti";
import logger from "../utils/logger";
import { keyframes } from "@emotion/react";
import { motion, AnimatePresence } from "framer-motion";
import { usePlayNavigation } from "../hooks/usePlayNavigation";
import { CheckIcon, CloseIcon } from "@chakra-ui/icons";
import { getLanguageCode } from "../utils/languageUtils";
import { WordTranslationProvider } from "../components/common/WordTranslationProvider";

// SRS imports
import { useSRS, type SRSSentence, type SRSReview } from "../hooks/useSRS";

// Map language names to Tatoeba language codes
const languageToCode: Record<string, string> = {
  english: "eng",
  hebrew: "heb",
  dutch: "nld",
  spanish: "spa",
  french: "fra",
  german: "deu",
  italian: "ita",
  portuguese: "por",
  russian: "rus",
  japanese: "jpn",
  chinese: "cmn",
  korean: "kor",
  arabic: "ara",
};

// Type for API responses
interface ApiResponse<T> {
  data: T | null;
  error: any | null;
}

// Type for favorites data
interface FavoriteData {
  sentence_id: number;
}

// Type for user settings
interface UserSettingsData {
  settings: {
    nativeLanguage: string;
  };
}

interface Sentence {
  id: string;
  word_id: string;
  sentence: string;
  translation: string;
  translation_lang?: string;
  times_seen: number;
  remembered: boolean;
  word: string;
  language: string;
  words?: { word: string; language: string };
  points: number;
  // SRS fields - make these required since we're using them in the UI
  srs_interval: number;
  srs_repetition: number;
  srs_easiness: number;
  srs_due_date?: string;
  srs_last_reviewed?: string;
  srs_review_count: number;
}

/**
 * Convert SRS easiness value to CEFR level
 */
function easinessToEFR(easiness: number): string {
  if (easiness >= 3.5) return "A1";
  if (easiness >= 3.0) return "A2";
  if (easiness >= 2.5) return "B1";
  if (easiness >= 2.0) return "B2";
  if (easiness >= 1.5) return "C1";
  return "C2";
}

/**
 * Get CEFR color scheme for badges
 */
function getCEFRColorScheme(cefr: string): string {
  switch (cefr) {
    case "A1":
      return "green";
    case "A2":
      return "teal";
    case "B1":
      return "blue";
    case "B2":
      return "purple";
    case "C1":
      return "orange";
    case "C2":
      return "red";
    default:
      return "gray";
  }
}

export default function Play() {
  // State variables
  const [showTranslation, setShowTranslation] = useState(false);
  const [showWord, setShowWord] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isFixingTranslation, setIsFixingTranslation] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [options, setOptions] = useState<string[]>([]);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [isAnswerSubmitted, setIsAnswerSubmitted] = useState(false);
  const [playFeedback, setPlayFeedback] = useState<{
    type: "correct" | "wrong" | null;
    message: string;
  }>({ type: null, message: "" });
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [reviewStartTime, setReviewStartTime] = useState<number | null>(null);
  const [successWord, setSuccessWord] = useState<string | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Add color mode values for the UI elements - moved to top of component
  const cardBg = useColorModeValue("white", "gray.800");
  const cardBorder = useColorModeValue("gray.200", "gray.700");
  const highlightColor = useColorModeValue("purple.500", "purple.300");
  const textColor = useColorModeValue("gray.800", "white");
  const translationBg = useColorModeValue("gray.50", "gray.700");
  const translationBorder = useColorModeValue("purple.100", "purple.800");
  const badgeBg = useColorModeValue("purple.100", "purple.900");

  // Background colors for the game card
  const gameBg = useColorModeValue("purple.50", "purple.900");
  const gameBgGradient = useColorModeValue(
    "linear(to-b, purple.100, white)",
    "linear(to-b, purple.800, gray.900)"
  );

  // Button styles
  const optionButtonHoverBg = useColorModeValue("purple.100", "purple.700");

  // Extract all other inline useColorModeValue calls
  const controlBarBg = useColorModeValue("gray.50", "gray.700");

  // Kbd styles for light/dark mode
  const kbdBg = useColorModeValue("gray.100", "gray.600");
  const kbdColor = useColorModeValue("gray.800", "gray.100");
  const kbdBorderColor = useColorModeValue("gray.200", "gray.500");

  // Refs
  const confettiRef = useRef<HTMLDivElement | null>(null);
  const correctSoundRef = useRef<HTMLAudioElement | null>(null);
  const wrongSoundRef = useRef<HTMLAudioElement | null>(null);

  // Hooks
  const {
    isOpen: isSuccessModalOpen,
    onOpen: onSuccessModalOpen,
    onClose: onSuccessModalClose,
  } = useDisclosure();
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();
  
  // SRS hooks
  const { getDueReviews, submitReview, submitReviewMutation, useReviewStats } =
    useSRS();
  const { data: srsStats } = useReviewStats();

  // Responsive grid columns for options
  const gridColumns = useBreakpointValue({ base: 1, md: 2 });

  // Animations
  const correctAnimation = keyframes`
    0% {
      transform: scale(0.8);
      opacity: 0;
    }
    50% {
      transform: scale(1.2);
      opacity: 1;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  `;

  const wrongAnimation = keyframes`
    0% {
      transform: scale(0.8);
      opacity: 0;
    }
    25% {
      transform: scale(1.1) rotate(-2deg);
      opacity: 1;
    }
    50% {
      transform: scale(1.1) rotate(2deg);
      opacity: 1;
    }
    75% {
      transform: scale(1.1) rotate(-2deg);
      opacity: 1;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  `;

  const popInAnimation = keyframes`
    0% {
      transform: scale(0) translateY(20px);
      opacity: 0;
    }
    70% {
      transform: scale(1.2) translateY(-10px);
      opacity: 1;
    }
    100% {
      transform: scale(1) translateY(0);
      opacity: 1;
    }
  `;

  // Replace the old sentences query with SRS-based due reviews
  const {
    data: sentences,
    isLoading,
    error,
    refetch: refetchSentences,
  } = useQuery({
    queryKey: ["srs-due-reviews", 20],
    queryFn: () => getDueReviews(20),
    enabled: !!user,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const { data: allWords } = useQuery({
    queryKey: ["all-words"],
    queryFn: async () => {
      const data = await apiClient.getWords();
      return data || [];
    },
    enabled: !!user,
  });

  const { data: favorites, isLoading: isFavoritesLoading } = useQuery({
    queryKey: ["favorites"],
    queryFn: async () => {
      try {
        const data = await apiClient.getFavorites();
        if (!data) return [];
        return data.map((fav: any) => fav.sentenceId);
      } catch (err) {
        logger.error("Error fetching favorites:", err);
        return [];
      }
    },
    enabled: !!user,
  });

  // Fix the 'favorites' possibly undefined linter warning
  const isFavorite =
    sentences && sentences.length > 0
      ? (favorites ?? []).includes(Number(sentences[0].id))
      : false;

  // Add/remove favorite mutation
  const toggleFavoriteMutation = useMutation({
    mutationFn: async ({
      sentenceId,
      isFavorite,
    }: {
      sentenceId: string;
      isFavorite: boolean;
    }) => {
      try {
        const result = await apiClient.toggleFavorite(Number(sentenceId));
        return result;
      } catch (err) {
        logger.error("Favorite mutation error:", err);
        throw err;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["favorites"] });
      queryClient.invalidateQueries({ queryKey: ["favorites-count"] });
      toast({
        title: !data.favorited
          ? t("play.removedFromFavorites")
          : t("play.addedToFavorites"),
        status: !data.favorited ? "info" : "success",
        duration: 2000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: t("common.error"),
        description:
          error instanceof Error ? error.message : t("play.favoriteError"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // Fetch all sentences for progress calculation
  const { data: allSentences } = useQuery({
    queryKey: ["all-sentences"],
    queryFn: async () => {
      const data = await apiClient.getSentences();
      return data || [];
    },
    enabled: !!user,
  });

  // Add a separate count query for total sentences
  const { data: totalSentencesCount } = useQuery({
    queryKey: ["all-sentences-count"],
    queryFn: async () => {
      const data = await apiClient.getSentences();
      return data?.length || 0;
    },
    enabled: !!user,
  });

  // Helper functions - defined before useEffects
  // Points progress
  const getPointsColor = (points: number) => {
    if (points < 25) return "red";
    if (points < 50) return "orange";
    if (points < 75) return "blue";
    return "green";
  };

  // Helper function to play sounds with error handling
  const playSound = useCallback((type: "correct" | "wrong") => {
    try {
      const soundRef =
        type === "correct" ? correctSoundRef.current : wrongSoundRef.current;
      if (soundRef) {
        soundRef.currentTime = 0;
        soundRef.play().catch((e) => {
          logger.warn(`Error playing ${type} sound:`, e);
        });
      }
    } catch (e) {
      logger.warn(`Error with ${type} sound:`, e);
    }
  }, []);

  // Celebration effects
  const triggerCelebration = useCallback(() => {
    if (confettiRef.current) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });
    }

    // Temporarily remove playSound call to fix crash
    // playSound('correct');

    // Remove the toast notification since we have the success modal
    // The modal already shows "Well done!" and "Word learned!" messages
  }, []);

  // Function to reset UI state when navigating
  const resetUIState = useCallback(() => {
    setShowWord(false);
    setShowTranslation(false);
    setIsHovering(false);
    setShowSuccess(false);
    setIsFixingTranslation(false);
    setOptions([]);
    setSelectedOption(null);
    setShowAnswer(false);
    setIsCorrect(null);
    setIsAnswerSubmitted(false);
    setPlayFeedback({ type: null, message: "" });
    setReviewStartTime(null);
  }, []);

  // Convert SRSSentence to Sentence type for compatibility
  const adaptedSentences: Sentence[] | null = React.useMemo(() => {
    if (!sentences) return null;

    return sentences.map(
      (srsSentence): Sentence => ({
        id: srsSentence.id,
        sentence: srsSentence.sentence,
        translation: srsSentence.translation,
        word_id: srsSentence.words?.word || "", // Use word as word_id fallback
        times_seen: 0, // Default value for legacy field
        remembered: false, // Default value for legacy field
        word: srsSentence.words?.word || "",
        language: srsSentence.words?.language || "english",
        words: srsSentence.words,
        points: 0, // Default value for legacy field
        // Include SRS fields with defaults
        srs_interval: srsSentence.srs_interval || 1,
        srs_repetition: srsSentence.srs_repetition || 0,
        srs_easiness: srsSentence.srs_easiness || 2.5,
        srs_due_date: srsSentence.srs_due_date,
        srs_last_reviewed: srsSentence.srs_last_reviewed,
        srs_review_count: srsSentence.srs_review_count || 0,
      })
    );
  }, [sentences]);

  const [
    { currentSentence, sentenceHistory, historyIndex, direction, animate },
    { goToNext, goBack, resetAnimationState, handleAfterAnimation, canGoBack },
  ] = usePlayNavigation(adaptedSentences, resetUIState);

  // Show success modal when a word is remembered (high difficulty rating)
  useEffect(() => {
    if (showSuccess) {
      onSuccessModalOpen();
      // Trigger celebration effects
      triggerCelebration();

      // Auto-close success message after delay - but don't auto-advance
      // since that's now handled by the difficulty rating system
      const timer = setTimeout(() => {
        setShowSuccess(false);
        setSuccessWord(null); // Clear the captured word
        onSuccessModalClose();
      }, 2000); // Changed from 2500ms to 2000ms (2 seconds)

      return () => clearTimeout(timer);
    }
  }, [
    showSuccess,
    onSuccessModalOpen,
    onSuccessModalClose,
    triggerCelebration,
  ]);

  // Initialize sound effects
  useEffect(() => {
    // Try to load sound effects with error handling
    try {
      correctSoundRef.current = new Audio("/sounds/correct.mp3");
      wrongSoundRef.current = new Audio("/sounds/wrong.mp3");

      // Add error handlers
      correctSoundRef.current.addEventListener("error", () => {
        logger.warn("Failed to load correct sound effect");
      });

      wrongSoundRef.current.addEventListener("error", () => {
        logger.warn("Failed to load wrong sound effect");
      });
    } catch (e) {
      logger.error("Error setting up sound effects:", e);
    }

    return () => {
      correctSoundRef.current = null;
      wrongSoundRef.current = null;
    };
  }, []);

  const shuffleArray = useCallback(<T,>(array: T[]): T[] => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  }, []);

  const generateOptions = useCallback(
    (correctWord: string, language: string) => {
      if (!allWords || allWords.length === 0) return [correctWord];

      const wordsInLanguage = allWords.filter(
        (w) => w.language === language && w.word !== correctWord
      );

      if (wordsInLanguage.length < 3) {
        const options = [correctWord, ...wordsInLanguage.map((w) => w.word)];
        return shuffleArray([...new Set(options)]);
      }

      const incorrectOptions: string[] = [];
      const usedIndexes = new Set<number>();

      while (
        incorrectOptions.length < 3 &&
        usedIndexes.size < wordsInLanguage.length
      ) {
        const randomIndex = Math.floor(Math.random() * wordsInLanguage.length);
        if (!usedIndexes.has(randomIndex)) {
          usedIndexes.add(randomIndex);
          incorrectOptions.push(wordsInLanguage[randomIndex].word);
        }
      }

      const options = [correctWord, ...incorrectOptions];
      return shuffleArray(options);
    },
    [allWords, shuffleArray]
  );

  // Generate options when current sentence changes
  useEffect(() => {
    if (currentSentence && currentSentence.words?.word && allWords) {
      const newOptions = generateOptions(
        currentSentence.words.word,
        currentSentence.language || currentSentence.words.language
      );
      setOptions(newOptions);
      setSelectedOption(null);
      setShowAnswer(false);
      setIsAnswerSubmitted(false);
      setIsCorrect(null);
    }
  }, [currentSentence, allWords, generateOptions]);

  // Handler functions (define these first!)
  const handleOptionSelect = useCallback(
    (option: string) => {
      if (isAnswerSubmitted) return;
      setSelectedOption(option);
    },
    [isAnswerSubmitted]
  );

  // Start review timer when sentence loads
  useEffect(() => {
    if (currentSentence && !reviewStartTime) {
      setReviewStartTime(Date.now());
    }
  }, [currentSentence, reviewStartTime]);

  // Reset review timer when moving to next sentence
  const resetReviewState = useCallback(() => {
    setReviewStartTime(null);
    setIsAnswerSubmitted(false);
    setShowAnswer(false);
    setIsCorrect(null);
    setSelectedOption(null);
    setPlayFeedback({ type: null, message: "" });
  }, []);

  // Function to automatically calculate difficulty based on answer correctness and response time
  const calculateAutomaticDifficulty = useCallback(
    (isCorrect: boolean, responseTimeMs: number): number => {
      // If answer is incorrect, assign low difficulty rating
      if (!isCorrect) {
        return 1; // "Incorrect, but remembered after seeing answer"
      }

      // For correct answers, determine difficulty based on response time
      const responseTimeSeconds = responseTimeMs / 1000;

      if (responseTimeSeconds <= 2) {
        return 5; // Perfect recall - instantly knew it
      } else if (responseTimeSeconds <= 5) {
        return 4; // Correct, with some hesitation
      } else if (responseTimeSeconds <= 10) {
        return 3; // Correct, but with difficulty/hesitation
      } else {
        return 2; // Took too long, but answer seemed familiar
      }
    },
    []
  );

  // Handle SRS difficulty rating submission
  const handleDifficultyRating = useCallback(
    async (difficulty: number) => {
      if (!currentSentence || !reviewStartTime) return;

      const responseTime = Date.now() - reviewStartTime;

      const review: SRSReview = {
        sentenceId: currentSentence.id,
        difficulty,
        responseTime,
      };

      try {
        await submitReview(review);

        // CRITICAL FIX: Wait for cache invalidation and refetch BEFORE navigating
        // This ensures the navigation logic has access to updated sentence data
        await queryClient.invalidateQueries({ queryKey: ["srs-due-reviews"] });
        await queryClient.invalidateQueries({ queryKey: ["srs-stats"] });

        // Force an immediate refetch to ensure fresh data
        await refetchSentences();

        // Show success feedback for excellent performance
        if (difficulty >= 4) {
          // Capture the word BEFORE navigation to prevent showing next sentence's word
          setSuccessWord(currentSentence.words?.word || "");
          setShowSuccess(true);

          // Reset state and move to next sentence after celebration
          setTimeout(() => {
            resetReviewState();
            goToNext();
          }, 2000); // Changed from 2500ms to 2000ms to match modal timeout
        } else {
          // For lower scores, just reset state without celebration
          setTimeout(() => {
            resetReviewState();
            goToNext();
          }, 1000);
        }
      } catch (error) {
        toast({
          title: t("common.error"),
          description: t("srs.reviewSubmissionError"),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    },
    [
      currentSentence,
      reviewStartTime,
      submitReview,
      resetReviewState,
      goToNext,
      queryClient,
      refetchSentences,
      toast,
      t,
    ]
  );

  // Update the handleSubmitAnswer callback to automatically determine difficulty instead of asking user
  const handleSubmitAnswer = useCallback(() => {
    if (!currentSentence || !selectedOption || isAnswerSubmitted) return;

    const correctAnswer = currentSentence.words?.word || "";
    const isCorrectAnswer = selectedOption === correctAnswer;
    setIsCorrect(isCorrectAnswer);
    setShowAnswer(true);
    setIsAnswerSubmitted(true);

    if (isCorrectAnswer) {
      playSound("correct");
    } else {
      playSound("wrong");
    }

    const responseTime = reviewStartTime ? Date.now() - reviewStartTime : 0;
    const autoDetectedDifficulty = calculateAutomaticDifficulty(
      isCorrectAnswer,
      responseTime
    );

    handleDifficultyRating(autoDetectedDifficulty);
  }, [
    currentSentence,
    selectedOption,
    isAnswerSubmitted,
    playSound,
    reviewStartTime,
    calculateAutomaticDifficulty,
    handleDifficultyRating,
  ]);

  // Define stubs for missing handlers if not implemented yet
  const handleFixTranslation = () => {
    setIsFixingTranslation(true);
    // Add your fix translation logic here
    // For now, just show a toast
    toast({
      title: t("play.fixTranslationRequested"),
      description: t("play.fixTranslationMessage"),
      status: "info",
      duration: 3000,
      isClosable: true,
    });

    setTimeout(() => {
      setIsFixingTranslation(false);
    }, 1000);
  };
  const handleToggleFavorite = () => {
    if (!currentSentence) return;

    toggleFavoriteMutation.mutate({
      sentenceId: currentSentence.id,
      isFavorite: isFavorite,
    });
  };
  const handleTTS = useCallback(() => {
    if (!currentSentence) return;

    try {
      // Create a new instance of TTSApiService
      const ttsService = new TTSApiService();

      // Get the language from the sentence
      let language = currentSentence.language || "";

      // If sentence language is missing but we have words.language, use that
      if (!language && currentSentence.words?.language) {
        language = currentSentence.words.language;
      }

      // If we have language as a name (e.g., "dutch"), convert it to a BCP-47 code
      // If we already have a BCP-47 code (e.g., "nl-NL"), it will be returned as is
      const langCode = getLanguageCode(language || "english");

      logger.info(
        `TTS for sentence: "${currentSentence.sentence.substring(
          0,
          30
        )}..." using language: ${language} (code: ${langCode})`
      );

      // Call the speak method with the current sentence
      ttsService
        .speak(currentSentence.sentence, {
          lang: langCode,
        })
        .catch((error) => {
          logger.error("TTS error:", error);
          // Display toast error notification
          toast({
            title: t("play.ttsError") || "TTS Error",
            description:
              t("play.ttsErrorDescription") || "Failed to play audio",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        });
    } catch (error) {
      logger.error("Error initializing TTS:", error);
    }
  }, [currentSentence, t, toast]);

  // Update the handleSkip to use our new hook
  const handleSkip = useCallback(() => {
    goToNext();
  }, [goToNext]);

  const handleDontRemember = useCallback(() => {
    if (currentSentence) {
      // Update sentence mutation - REMOVED: Now using SRS reviews instead
      // updateSentenceMutation.mutate({
      //   sentenceId: currentSentence.id,
      //   remembered: false,
      //   timesSeen: currentSentence.times_seen + 1,
      //   points: 0
      // });
    }
  }, [currentSentence]);

  const handleRemember = useCallback(() => {
    if (currentSentence) {
      // Update sentence mutation - REMOVED: Now using SRS reviews instead
      // updateSentenceMutation.mutate({
      //   sentenceId: currentSentence.id,
      //   remembered: true,
      //   timesSeen: currentSentence.times_seen + 1,
      //   points: 100
      // });
    }
  }, [currentSentence]);

  // Helper to get a sentence with the target word hidden
  const getHiddenWordSentence = useCallback(
    (sentence: string, targetWord: string) => {
      if (!targetWord || !sentence) return sentence;

      // Check if we need to use words.word property
      const actualTargetWord = targetWord;

      // Generate a matching number of underscores based on word length
      const underscores = "_".repeat(actualTargetWord.length);

      // Handle different languages differently
      const isHebrew = /[\u0590-\u05FF]/.test(actualTargetWord); // Check if the word contains Hebrew characters

      if (isHebrew) {
        // For Hebrew, do a simple direct replacement
        try {
          // Direct string replacement without regex
          if (sentence.includes(actualTargetWord)) {
            return sentence.replace(actualTargetWord, underscores);
          }

          // If the direct replacement didn't work, try a non-case-sensitive approach
          const lowerSentence = sentence.toLowerCase();
          const lowerWord = actualTargetWord.toLowerCase();

          if (lowerSentence.includes(lowerWord)) {
            // Find the word position and replace it
            const startIndex = lowerSentence.indexOf(lowerWord);
            const endIndex = startIndex + lowerWord.length;
            return (
              sentence.substring(0, startIndex) +
              underscores +
              sentence.substring(endIndex)
            );
          }

          return sentence;
        } catch (e) {
          logger.error("Error replacing Hebrew word:", e);
          return sentence;
        }
      } else {
        // For Latin-based languages, use the regex approach
        try {
          // Make the regex case insensitive and handle word variations
          const wordPattern = actualTargetWord.replace(
            /[-/\\^$*+?.()|[\]{}]/g,
            "\\$&"
          );
          const regex = new RegExp(`\\b${wordPattern}\\b`, "i");

          // Check if the exact word is in the sentence
          if (regex.test(sentence)) {
            return sentence.replace(regex, underscores);
          }

          // Try a more flexible match in case of conjugations
          const rootPattern =
            actualTargetWord.length > 3
              ? actualTargetWord
                  .substring(0, actualTargetWord.length - 1)
                  .replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")
              : wordPattern;
          const flexibleRegex = new RegExp(`\\b${rootPattern}\\w*\\b`, "i");

          if (flexibleRegex.test(sentence)) {
            // When using the flexible regex, we don't know the exact length
            // of the word being replaced, so capture it and use its length
            const match = sentence.match(flexibleRegex);
            const matchedWord = match ? match[0] : actualTargetWord;
            const matchUnderscores = "_".repeat(matchedWord.length);

            return sentence.replace(flexibleRegex, matchUnderscores);
          }
        } catch (e) {
          logger.error("Error replacing word with regex:", e);
        }
      }

      // Return original if word not found or on error
      return sentence;
    },
    []
  );

  // Determine if the sentence is in Hebrew
  const isHebrewSentence = useCallback((sentence: string) => {
    return /[\u0590-\u05FF]/.test(sentence);
  }, []);

  // Card animation variants for framer-motion
  const cardVariants = {
    enter: (direction: "forward" | "backward") => {
      logger.debug(`[Variant:enter] Animating with direction: ${direction}`);
      return {
        x: direction === "forward" ? 300 : -300,
        opacity: 0,
      };
    },
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: "forward" | "backward") => {
      logger.debug(`[Variant:exit] Animating with direction: ${direction}`);
      return {
        x: direction === "forward" ? -300 : 300,
        opacity: 0,
      };
    },
  };

  const renderProgressStats = () => {
    if (!sentences) return null;

    // For SRS, show due reviews count and total reviews
    const dueReviewsCount = sentences?.length || 0;

    // Use SRS stats if available
    const totalReviews = srsStats?.totalReviews || 0;
    const reviewsToday = srsStats?.reviewsToday || 0;

    return (
      <Flex align="center">
        <CircularProgress
          value={
            dueReviewsCount > 0
              ? Math.min(
                  100,
                  (reviewsToday / Math.max(dueReviewsCount, 1)) * 100
                )
              : 0
          }
          color="purple.400"
          size={{ base: "30px", md: "40px" }}
          thickness="8px"
        >
          <CircularProgressLabel
            fontWeight="bold"
            fontSize={{ base: "2xs", md: "xs" }}
          >
            {dueReviewsCount}
          </CircularProgressLabel>
        </CircularProgress>
        <Box ml={{ base: 1, md: 2 }}>
          <Text fontSize={{ base: "2xs", md: "xs" }} fontWeight="medium">
            {t("srs.dueReviews", { count: dueReviewsCount })}
          </Text>
          <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500">
            {t("srs.reviewsToday", { count: reviewsToday })}
          </Text>
        </Box>
      </Flex>
    );
  };

  // Handler refs for keyboard shortcuts
  const optionSelectRef = useRef(handleOptionSelect);
  const submitAnswerRef = useRef(handleSubmitAnswer);
  const nextWordRef = useRef(goToNext);
  const goBackRef = useRef(goBack);
  const canGoBackRef = useRef(canGoBack);
  const optionsRef = useRef(options);
  const selectedOptionRef = useRef(selectedOption);
  const isAnswerSubmittedRef = useRef(isAnswerSubmitted);
  const showSuccessRef = useRef(showSuccess);
  const currentSentenceRef = useRef(currentSentence);

  useEffect(() => {
    optionSelectRef.current = handleOptionSelect;
    submitAnswerRef.current = handleSubmitAnswer;
    nextWordRef.current = goToNext;
    goBackRef.current = goBack;
    canGoBackRef.current = canGoBack;
    optionsRef.current = options;
    selectedOptionRef.current = selectedOption;
    isAnswerSubmittedRef.current = isAnswerSubmitted;
    showSuccessRef.current = showSuccess;
    currentSentenceRef.current = currentSentence;
  }, [
    handleOptionSelect,
    handleSubmitAnswer,
    goToNext,
    goBack,
    canGoBack,
    options,
    selectedOption,
    isAnswerSubmitted,
    showSuccess,
    currentSentence,
  ]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (showSuccessRef.current || !currentSentenceRef.current) return;
      if (e.key === "ArrowRight") {
        nextWordRef.current();
      } else if (e.key === "ArrowLeft" && canGoBackRef.current) {
        goBackRef.current();
      } else if (
        !isAnswerSubmittedRef.current &&
        optionsRef.current.length > 0 &&
        ["1", "2", "3", "4"].includes(e.key)
      ) {
        const index = parseInt(e.key) - 1;
        if (index < optionsRef.current.length) {
          optionSelectRef.current(optionsRef.current[index]);
        }
      } else if (
        e.key === "Enter" &&
        selectedOptionRef.current &&
        !isAnswerSubmittedRef.current
      ) {
        submitAnswerRef.current();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Report and delete a sentence
  const handleReportSentence = async () => {
    if (!currentSentence) return;

    try {
      await apiClient.deleteSentence(currentSentence.id);

      toast({
        title: t("play.sentenceReported"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Go to next sentence
      goToNext();

      // Close dialog
      setIsReportDialogOpen(false);

      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ["sentences"] });
    } catch (error) {
      console.error("Error reporting sentence:", error);
      toast({
        title: t("play.reportError"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Add mobile scroll lock effect
  useEffect(() => {
    const checkMobile = () => window.innerWidth <= 768;
    const isMobile = checkMobile();

    if (isMobile) {
      // Lock scroll on mobile devices
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const originalWidth = document.body.style.width;
      const originalHeight = document.body.style.height;

      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.height = "100%";
      document.body.style.top = "0";
      document.body.style.left = "0";

      console.log("Mobile scroll lock applied");

      return () => {
        // Cleanup scroll lock
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.width = originalWidth;
        document.body.style.height = originalHeight;
        document.body.style.top = "";
        document.body.style.left = "";
        console.log("Mobile scroll lock removed");
      };
    }

    // Also listen for resize events to handle orientation changes
    const handleResize = () => {
      const nowMobile = checkMobile();
      if (nowMobile !== isMobile) {
        // Trigger re-render by updating a state if needed
        window.location.reload(); // Simple approach for now
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // This function will decide what to render
  const renderContent = () => {
    // Not logged in
    if (!user) {
      return (
        <Container maxW="container.md" py={10}>
          <VStack spacing={6}>
            <Heading>{t("common.pleaseLogin")}</Heading>
            <Button colorScheme="purple" onClick={() => navigate("/login")}>
              {t("common.goToLogin")}
            </Button>
          </VStack>
        </Container>
      );
    }

    // Loading state
    if (isLoading || isFavoritesLoading) {
      return (
        <Container maxW="container.md" py={10}>
          <VStack spacing={6}>
            <Progress size="xs" isIndeterminate w="full" colorScheme="purple" />
            <Text>{t("common.loading")}</Text>
          </VStack>
        </Container>
      );
    }

    // No sentences
    if (!sentences || sentences.length === 0) {
      return (
        <Container maxW="container.md" py={10}>
          <VStack spacing={6}>
            <Heading>{t("common.noSentencesToLearn")}</Heading>
            <Text>{t("common.addWordsFirst")}</Text>
            <Button colorScheme="purple" onClick={() => navigate("/dashboard")}>
              {t("common.goToDashboard")}
            </Button>
          </VStack>
        </Container>
      );
    }

    // Main game content - this is the previous return statement content
    return (
      <Container
        maxW="container.md"
        py={{ base: 1, md: 10 }}
        px={{ base: 1, md: 6 }}
        ref={confettiRef}
        h={{ base: "100vh", md: "auto" }}
        display="flex"
        flexDirection="column"
        overflow={{ base: "hidden", md: "visible" }}
      >
        <VStack spacing={{ base: 1, md: 8 }} flex={1} h="full">
          <Flex
            justify="space-between"
            width="full"
            alignItems="center"
            py={{ base: 1, md: 0 }}
            minH={{ base: "auto", md: "auto" }}
          >
            <HStack>
              {/* Mobile back button */}
              <IconButton
                aria-label="Back to Dashboard"
                icon={<FiBook />}
                variant="ghost"
                size="sm"
                display={{ base: "flex", md: "none" }}
                onClick={() => navigate("/dashboard")}
                _hover={{ transform: "translateY(-2px)" }}
              />
              <Heading
                display="flex"
                alignItems="center"
                bgGradient="linear(to-l, #7928CA, #FF0080)"
                bgClip="text"
                fontSize={{ base: "lg", md: "3xl" }}
                fontWeight="extrabold"
              >
                <Icon as={FaGamepad} mr={2} boxSize={{ base: 4, md: 6 }} />
                {t("common.learningSession")}
              </Heading>
            </HStack>
            <Box ml={2} minWidth={{ base: "100px", md: "180px" }}>
              {renderProgressStats()}
            </Box>
          </Flex>

          {/* Success modal */}
          <Modal
            isOpen={isSuccessModalOpen}
            onClose={onSuccessModalClose}
            isCentered
          >
            <ModalOverlay />
            <ModalContent bg="green.50" borderRadius="xl" p={2}>
              <ModalHeader textAlign="center" color="green.700">
                <Icon as={FaCrown} boxSize={12} color="yellow.400" mb={4} />
                <Heading size="lg">{t("play.wellDone")}</Heading>
              </ModalHeader>
              <ModalBody>
                <VStack spacing={4}>
                  <Text
                    fontSize="xl"
                    fontWeight="bold"
                    textAlign="center"
                    color="green.700"
                  >
                    {t("play.wordLearned")}
                  </Text>
                  {successWord && (
                    <Text fontWeight="bold" fontSize="2xl" textAlign="center">
                      {successWord}
                    </Text>
                  )}
                </VStack>
              </ModalBody>
            </ModalContent>
          </Modal>

          <AnimatePresence
            initial={false}
            mode="wait"
            onExitComplete={handleAfterAnimation}
          >
            {currentSentence && !showSuccess && (
              <motion.div
                key={currentSentence.id}
                custom={direction}
                variants={cardVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 },
                }}
                style={{
                  width: "100%",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Card
                  width="full"
                  bg={gameBg}
                  bgGradient={gameBgGradient}
                  borderColor={cardBorder}
                  borderWidth="1px"
                  borderRadius={{ base: "md", md: "xl" }}
                  boxShadow="xl"
                  overflow="hidden"
                  flex={1}
                  display="flex"
                  flexDirection="column"
                  maxH={{ base: "calc(100vh - 100px)", md: "auto" }}
                >
                  <Box bg={highlightColor} h="6px" w="full" />
                  <CardBody
                    p={{ base: 2, md: 6 }}
                    flex={1}
                    display="flex"
                    flexDirection="column"
                  >
                    <VStack spacing={{ base: 1, md: 6 }} flex={1}>
                      <Box position="relative" width="full">
                        <Flex
                          justifyContent="space-between"
                          mb={{ base: 1, md: 3 }}
                          alignItems="center"
                          flexWrap={{ base: "wrap", md: "nowrap" }}
                          gap={{ base: 1, md: 0 }}
                        >
                          <Badge
                            colorScheme="purple"
                            fontSize={{ base: "2xs", md: "sm" }}
                            px={1}
                            py={0.5}
                          >
                            {currentSentence.language}
                          </Badge>
                          <HStack
                            spacing={{ base: 0.5, md: 2 }}
                            flexWrap="wrap"
                          >
                            <Badge
                              colorScheme="blue"
                              fontSize={{ base: "2xs", md: "sm" }}
                            >
                              {t("srs.reviews")}:{" "}
                              {currentSentence.srs_review_count ?? 0}
                            </Badge>
                            <Badge
                              colorScheme="green"
                              fontSize={{ base: "2xs", md: "sm" }}
                            >
                              {t("srs.interval")}:{" "}
                              {currentSentence.srs_interval ?? 1}d
                            </Badge>
                            <Badge
                              colorScheme={getCEFRColorScheme(
                                easinessToEFR(
                                  currentSentence.srs_easiness ?? 2.5
                                )
                              )}
                              fontSize={{ base: "2xs", md: "sm" }}
                            >
                              {t("srs.easiness")}:{" "}
                              {easinessToEFR(
                                currentSentence.srs_easiness ?? 2.5
                              )}
                            </Badge>
                          </HStack>
                        </Flex>

                        <SlideFade in={true} offsetY="20px">
                          <WordTranslationProvider
                            sourceLanguage={currentSentence.language}
                            preloadedTranslations={[]}
                          >
                            {(formatContent) => (
                              <Box
                                fontSize={{ base: "md", md: "2xl" }}
                                fontWeight="bold"
                                p={{ base: 2, md: 5 }}
                                borderRadius="md"
                                bg={translationBg}
                                borderWidth="1px"
                                borderColor={cardBorder}
                                width="full"
                                textAlign={
                                  isHebrewSentence(currentSentence.sentence)
                                    ? "right"
                                    : "left"
                                }
                                color={textColor}
                                dir={
                                  isHebrewSentence(currentSentence.sentence)
                                    ? "rtl"
                                    : "ltr"
                                }
                                boxShadow="sm"
                                minH={{ base: "40px", md: "auto" }}
                                maxH={{ base: "80px", md: "none" }}
                                overflow={{ base: "hidden", md: "visible" }}
                                lineHeight={{ base: "shorter", md: "normal" }}
                              >
                                {formatContent(
                                  showWord
                                    ? currentSentence.sentence
                                    : getHiddenWordSentence(
                                        currentSentence.sentence,
                                        currentSentence.words?.word || ""
                                      )
                                )}
                              </Box>
                            )}
                          </WordTranslationProvider>
                        </SlideFade>
                      </Box>

                      {showTranslation && (
                        <SlideFade in={showTranslation} offsetY="20px">
                          <Box
                            p={{ base: 2, md: 4 }}
                            borderRadius="md"
                            bg={translationBg}
                            borderWidth="1px"
                            borderColor={translationBorder}
                            width="full"
                            maxH={{ base: "60px", md: "none" }}
                            overflow={{ base: "hidden", md: "visible" }}
                          >
                            <Text
                              fontStyle="italic"
                              fontWeight="medium"
                              fontSize={{ base: "xs", md: "md" }}
                            >
                              {currentSentence.translation}
                            </Text>
                            <Button
                              size="sm"
                              leftIcon={<FiEdit />}
                              onClick={() => handleFixTranslation()}
                              variant="ghost"
                              isLoading={isFixingTranslation}
                              mt={1}
                              colorScheme="purple"
                              className="css-1vfscc3"
                              display="flex"
                            >
                              {t("play.fixTranslation")}
                            </Button>
                          </Box>
                        </SlideFade>
                      )}

                      {/* Feedback message */}
                      {playFeedback.type && (
                        <Box
                          position="absolute"
                          top="50%"
                          left="0"
                          right="0"
                          zIndex={10}
                          pointerEvents="none"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Box
                            p={4}
                            borderRadius="xl"
                            bg={
                              playFeedback.type === "correct"
                                ? "rgba(0, 200, 0, 0.9)"
                                : "rgba(255, 50, 50, 0.9)"
                            }
                            color="white"
                            textAlign="center"
                            fontWeight="bold"
                            fontSize="xl"
                            animation={`${
                              playFeedback.type === "correct"
                                ? popInAnimation
                                : wrongAnimation
                            } 0.5s ease-in-out`}
                            boxShadow="0 4px 20px rgba(0,0,0,0.2)"
                            minWidth="150px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            gap={2}
                          >
                            {playFeedback.type === "correct" ? (
                              <Icon as={CheckIcon} w={6} h={6} />
                            ) : (
                              <Icon as={CloseIcon} w={6} h={6} />
                            )}
                            {playFeedback.message}
                          </Box>
                        </Box>
                      )}

                      <Divider display={{ base: "none", md: "block" }} />

                      {/* Word Options */}
                      <VStack
                        spacing={{ base: 1, md: 4 }}
                        width="full"
                        flex={1}
                      >
                        <SimpleGrid
                          columns={gridColumns}
                          spacing={{ base: 1, md: 4 }}
                          width="full"
                        >
                          {options.map((option, index) => {
                            // Button color calculation for different states
                            const isCorrectAnswer =
                              option === currentSentence?.words?.word;
                            const isUserSelected = selectedOption === option;
                            const hasAnswered = showAnswer;

                            // Set button styles based on game state
                            let buttonStyle = {
                              bg: "gray.50",
                              borderColor: "gray.300",
                              color: "gray.800",
                              animation: undefined as string | undefined,
                              boxShadow: undefined as string | undefined,
                              customBorderWidth: undefined as
                                | string
                                | undefined,
                            };

                            if (hasAnswered) {
                              if (isCorrectAnswer) {
                                buttonStyle = {
                                  bg: "green.100",
                                  borderColor: "green.500",
                                  color: "green.800",
                                  animation: `${correctAnimation} 0.5s ease-in-out`,
                                  boxShadow: "0 0 15px rgba(0, 200, 0, 0.5)",
                                  customBorderWidth: "2px",
                                };
                              } else if (isUserSelected) {
                                buttonStyle = {
                                  bg: "red.100",
                                  borderColor: "red.500",
                                  color: "red.800",
                                  animation: `${wrongAnimation} 0.5s ease-in-out`,
                                  boxShadow: "0 0 15px rgba(255, 50, 50, 0.5)",
                                  customBorderWidth: "2px",
                                };
                              }
                            } else if (isUserSelected) {
                              buttonStyle = {
                                bg: "blue.100",
                                borderColor: "blue.500",
                                color: "blue.800",
                                animation: undefined,
                                boxShadow: undefined,
                                customBorderWidth: undefined,
                              };
                            }

                            return (
                              <Button
                                key={index}
                                onClick={() => handleOptionSelect(option)}
                                p={{ base: 1, md: 4 }}
                                height={{ base: "35px", md: "60px" }}
                                whiteSpace="normal"
                                textAlign="center"
                                borderWidth={
                                  buttonStyle.customBorderWidth || "1px"
                                }
                                borderRadius="lg"
                                borderColor={buttonStyle.borderColor}
                                bg={buttonStyle.bg}
                                color={buttonStyle.color}
                                _hover={{
                                  bg: !selectedOption
                                    ? "gray.100"
                                    : buttonStyle.bg,
                                  transform: !isAnswerSubmitted
                                    ? "translateY(-2px)"
                                    : "none",
                                  boxShadow: !isAnswerSubmitted ? "md" : "none",
                                }}
                                isDisabled={isAnswerSubmitted}
                                fontSize={{ base: "sm", md: "lg" }}
                                fontWeight="bold"
                                transition="all 0.2s"
                                sx={{
                                  animation: buttonStyle.animation,
                                  boxShadow: buttonStyle.boxShadow,
                                }}
                                position="relative"
                              >
                                {option}
                                {/* Show keyboard shortcut hint */}
                                <Text
                                  position="absolute"
                                  bottom="1"
                                  left="1"
                                  fontSize="2xs"
                                  opacity="0.7"
                                  display={{ base: "none", md: "inline" }}
                                >
                                  <Kbd
                                    bg={kbdBg}
                                    color={kbdColor}
                                    borderColor={kbdBorderColor}
                                    borderWidth="1px"
                                    px={1}
                                  >
                                    {index + 1}
                                  </Kbd>
                                </Text>
                                {showAnswer && isCorrectAnswer && (
                                  <Icon
                                    as={FaCheck}
                                    color="green.600"
                                    position="absolute"
                                    top="1"
                                    right="1"
                                    boxSize={3}
                                  />
                                )}
                                {showAnswer &&
                                  isUserSelected &&
                                  !isCorrectAnswer && (
                                    <Icon
                                      as={FaTimes}
                                      color="red.600"
                                      position="absolute"
                                      top="1"
                                      right="1"
                                      boxSize={3}
                                    />
                                  )}
                              </Button>
                            );
                          })}
                        </SimpleGrid>

                        {!isAnswerSubmitted ? (
                          <Button
                            colorScheme="purple"
                            size={{ base: "sm", md: "lg" }}
                            width="full"
                            onClick={handleSubmitAnswer}
                            isDisabled={!selectedOption}
                            _hover={{
                              transform: "translateY(-2px)",
                              boxShadow: "lg",
                            }}
                            _active={{ transform: "translateY(-1px)" }}
                            transition="all 0.2s"
                            fontWeight="bold"
                          >
                            {t("common.submit")}
                            {/* Use top-level Kbd styles */}
                            <Text
                              ml={2}
                              opacity="0.7"
                              display={{ base: "none", md: "inline" }}
                            >
                              <Kbd
                                bg={kbdBg}
                                color={kbdColor}
                                borderColor={kbdBorderColor}
                                borderWidth="1px"
                                px={1.5}
                              >
                                Enter
                              </Kbd>
                            </Text>
                          </Button>
                        ) : (
                          <Button
                            colorScheme="teal"
                            size={{ base: "sm", md: "lg" }}
                            width="full"
                            onClick={goToNext}
                            isDisabled={
                              animate || submitReviewMutation.isPending
                            }
                            isLoading={submitReviewMutation.isPending}
                            _hover={{
                              transform: "translateY(-2px)",
                              boxShadow: "lg",
                            }}
                            _active={{ transform: "translateY(-1px)" }}
                            transition="all 0.2s"
                            fontWeight="bold"
                          >
                            {submitReviewMutation.isPending
                              ? t("common.loading")
                              : t("learn.nextCard")}
                            {/* Use top-level Kbd styles */}
                            <Text
                              ml={2}
                              opacity="0.7"
                              display={{ base: "none", md: "inline" }}
                            >
                              <Kbd
                                bg={kbdBg}
                                color={kbdColor}
                                borderColor={kbdBorderColor}
                                borderWidth="1px"
                                px={1.5}
                              >
                                →
                              </Kbd>
                            </Text>
                          </Button>
                        )}
                      </VStack>

                      <Divider display={{ base: "none", md: "block" }} />

                      {/* Control Bar with Emoji Buttons */}
                      <Flex
                        width="full"
                        justifyContent="space-between"
                        px={{ base: 0, md: 4 }}
                        flexWrap="wrap"
                        gap={{ base: 1, md: 2 }}
                        bg={controlBarBg}
                        p={{ base: 2, md: 3 }}
                        borderRadius="lg"
                      >
                        <HStack
                          spacing={{ base: 1, md: 2 }}
                          flexWrap="wrap"
                          justifyContent="center"
                        >
                          <Tooltip label={t("play.markAsRemembered")} hasArrow>
                            <IconButton
                              aria-label={t("play.markAsRemembered")}
                              icon={
                                <Text fontSize={{ base: "md", md: "xl" }}>
                                  🧠
                                </Text>
                              }
                              colorScheme="green"
                              onClick={handleRemember}
                              isLoading={submitReviewMutation.isPending}
                              size={{ base: "sm", md: "md" }}
                              borderRadius="full"
                              _hover={{
                                transform: "translateY(-2px)",
                                boxShadow: "md",
                              }}
                            />
                          </Tooltip>

                          <Tooltip label={t("play.dontRemember")} hasArrow>
                            <IconButton
                              aria-label={t("play.dontRemember")}
                              icon={
                                <Text fontSize={{ base: "md", md: "xl" }}>
                                  🤷‍♂️
                                </Text>
                              }
                              colorScheme="red"
                              onClick={handleDontRemember}
                              isLoading={submitReviewMutation.isPending}
                              size={{ base: "sm", md: "md" }}
                              borderRadius="full"
                              _hover={{
                                transform: "translateY(-2px)",
                                boxShadow: "md",
                              }}
                            />
                          </Tooltip>

                          <Tooltip
                            label={
                              t("play.goBackToPrevious") ||
                              "Go back to previous"
                            }
                            hasArrow
                          >
                            <IconButton
                              aria-label={
                                t("play.goBackToPrevious") ||
                                "Go back to previous"
                              }
                              icon={
                                <Text fontSize={{ base: "md", md: "xl" }}>
                                  ⏮️
                                </Text>
                              }
                              colorScheme="gray"
                              onClick={goBack}
                              size={{ base: "sm", md: "md" }}
                              borderRadius="full"
                              isDisabled={!canGoBack || animate}
                              _hover={{
                                transform: "translateY(-2px)",
                                boxShadow: "md",
                              }}
                            />
                          </Tooltip>

                          <Tooltip label={t("play.skipToNext")} hasArrow>
                            <IconButton
                              aria-label={t("play.skipToNext")}
                              icon={
                                <Text fontSize={{ base: "md", md: "xl" }}>
                                  ⏭️
                                </Text>
                              }
                              colorScheme="gray"
                              onClick={handleSkip}
                              size={{ base: "sm", md: "md" }}
                              borderRadius="full"
                              isDisabled={animate}
                              _hover={{
                                transform: "translateY(-2px)",
                                boxShadow: "md",
                              }}
                            />
                          </Tooltip>

                          {/* Use top-level Kbd styles */}
                          <Box
                            fontSize="xs"
                            color="gray.500"
                            display={{ base: "none", md: "inline" }}
                          >
                            <Kbd
                              mr={1}
                              bg={kbdBg}
                              color={kbdColor}
                              borderColor={kbdBorderColor}
                              borderWidth="1px"
                              px={1.5}
                            >
                              ←
                            </Kbd>
                            <Kbd
                              bg={kbdBg}
                              color={kbdColor}
                              borderColor={kbdBorderColor}
                              borderWidth="1px"
                              px={1.5}
                            >
                              →
                            </Kbd>
                          </Box>
                        </HStack>

                        <HStack
                          spacing={{ base: 1, md: 2 }}
                          flexWrap="wrap"
                          justifyContent="center"
                        >
                          <Tooltip label={t("play.reportSentence")} hasArrow>
                            <IconButton
                              aria-label={t("play.reportSentence")}
                              icon={<FaFlag />}
                              variant="ghost"
                              onClick={() => setIsReportDialogOpen(true)}
                              isDisabled={!currentSentence}
                              size={{ base: "sm", md: "md" }}
                              borderRadius="full"
                              _hover={{ transform: "translateY(-2px)" }}
                            />
                          </Tooltip>

                          <Tooltip
                            label={
                              isFavorite
                                ? t("common.removeFromFavorites")
                                : t("common.addToFavorites")
                            }
                            hasArrow
                          >
                            <IconButton
                              aria-label={t("common.toggleFavorite")}
                              icon={
                                <Text fontSize={{ base: "md", md: "xl" }}>
                                  {isFavorite ? "❤️" : "🤍"}
                                </Text>
                              }
                              variant="ghost"
                              onClick={() => handleToggleFavorite()}
                              isDisabled={!currentSentence}
                              size={{ base: "sm", md: "md" }}
                              borderRadius="full"
                              _hover={{ transform: "translateY(-2px)" }}
                            />
                          </Tooltip>

                          <Tooltip
                            label={
                              showWord ? t("play.hideWord") : t("play.showWord")
                            }
                            hasArrow
                          >
                            <IconButton
                              aria-label={t("play.showWord")}
                              icon={
                                <Text fontSize={{ base: "md", md: "xl" }}>
                                  👁️
                                </Text>
                              }
                              variant="ghost"
                              onClick={() => setShowWord(!showWord)}
                              size={{ base: "sm", md: "md" }}
                              borderRadius="full"
                              _hover={{ transform: "translateY(-2px)" }}
                            />
                          </Tooltip>

                          <Tooltip
                            label={
                              showTranslation
                                ? t("play.hideTranslation")
                                : t("play.translate")
                            }
                            hasArrow
                          >
                            <IconButton
                              aria-label={t("play.translate")}
                              icon={
                                <Text fontSize={{ base: "md", md: "xl" }}>
                                  🌍
                                </Text>
                              }
                              variant="ghost"
                              onClick={() =>
                                setShowTranslation(!showTranslation)
                              }
                              size={{ base: "sm", md: "md" }}
                              borderRadius="full"
                              _hover={{ transform: "translateY(-2px)" }}
                            />
                          </Tooltip>

                          <Tooltip label={t("play.listen")} hasArrow>
                            <IconButton
                              aria-label={t("play.listen")}
                              icon={
                                <Text fontSize={{ base: "md", md: "xl" }}>
                                  🔊
                                </Text>
                              }
                              variant="ghost"
                              onClick={() => handleTTS()}
                              isDisabled={!currentSentence}
                              size={{ base: "sm", md: "md" }}
                              borderRadius="full"
                              _hover={{ transform: "translateY(-2px)" }}
                            />
                          </Tooltip>
                        </HStack>
                      </Flex>
                    </VStack>
                  </CardBody>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          <Button
            colorScheme="purple"
            onClick={() => navigate("/dashboard")}
            leftIcon={<FiBook />}
            size={{ base: "md", md: "lg" }}
            boxShadow="md"
            _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
            display={{ base: "none", md: "flex" }}
          >
            {t("common.backToDashboard")}
          </Button>
        </VStack>
        {/* Report & Delete Confirmation Dialog */}
        <AlertDialog
          isOpen={isReportDialogOpen}
          leastDestructiveRef={cancelRef}
          onClose={() => setIsReportDialogOpen(false)}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                {t("play.reportConfirmTitle")}
              </AlertDialogHeader>
              <AlertDialogBody>
                {t("play.reportConfirmMessage")}
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button
                  ref={cancelRef}
                  onClick={() => setIsReportDialogOpen(false)}
                >
                  {t("common.cancel")}
                </Button>
                <Button colorScheme="red" onClick={handleReportSentence} ml={3}>
                  {t("common.delete")}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Container>
    );
  };

  // Always return the renderContent result to ensure consistent hook execution
  return renderContent();
}
